import type { MarkerPhotoState } from "@/utils/plan-analysis/types";

/**
 * Rapprochement photos ↔ marqueurs photo d'une observation.
 *
 * Le lien est porté par le marqueur : il liste les id des photos qu'il illustre
 * (`photo-link.ts`). Ce lien prime sur tout le reste.
 *
 * Reste ce qui n'a pas encore de lien, à commencer par les anciens plans. Le
 * numéro d'une photo n'étant pas une identité, le rapprochement se lit alors à
 * l'intérieur de l'observation, sans rien enregistrer :
 *  - un seul marqueur : toutes les photos de l'observation sont les siennes ;
 *  - plusieurs marqueurs : le numéro exact de la photo départage. Une photo qui
 *    ne désigne pas un marqueur unique laisse les marqueurs restants « à
 *    confirmer », rien n'est deviné.
 *
 * Un id qui ne désigne aucune photo de l'observation (photo supprimée, marqueur
 * passé dans une autre observation) est ignoré. Pas de rapprochement hors
 * observation.
 */

/** Entité de l'observation, telle que lue sur le plan. */
export interface MatchableEntity {
  handle: string;
  markerNumber: number | null;
  /** Libellé du marqueur, `null` si l'entité n'est pas un marqueur photo. */
  markerLabel: string | null;
  /** Photos portées par le marqueur. */
  photoIds: readonly number[];
}

export interface MatchablePhoto {
  id: number;
  photoNumber?: number | null;
}

export interface MarkerSlot {
  handle: string;
  number: number | null;
  label: string;
}

export interface ObservationPhotoMatch {
  /** État photo de chaque marqueur, par handle. */
  markerStates: Map<string, MarkerPhotoState>;
  /** Marqueurs sans photo attribuée : une case d'attente chacun. */
  uncoveredMarkers: MarkerSlot[];
  /** Marqueurs proposés au choix sur une photo. Vide avec un seul marqueur. */
  choosableMarkers: MarkerSlot[];
  /** Marqueurs illustrés par chaque photo, par id (lien ou rapprochement). */
  markersByPhoto: Map<number, string[]>;
  /** Paires seulement déduites : à enregistrer avant de changer l'observation. */
  presumedLinks: { handle: string; photoId: number }[];
  /** Marqueur sur lequel enregistrer une photo ajoutée sans cible. */
  soleMarkerHandle: string | null;
}

const slotOf = (marker: MatchableEntity): MarkerSlot => ({
  handle: marker.handle,
  number: marker.markerNumber,
  label: (marker.markerLabel as string).trim(),
});

export function matchObservationPhotos(
  entities: readonly MatchableEntity[],
  photos: readonly MatchablePhoto[],
): ObservationPhotoMatch {
  const markers = entities.filter((e) => e.markerLabel !== null);
  const photoById = new Map(photos.map((photo) => [photo.id, photo]));
  const covered = new Set<string>();
  const toConfirm = new Set<string>();
  const markersByPhoto = new Map<number, string[]>(
    photos.map((photo) => [photo.id, []]),
  );
  const presumedLinks: ObservationPhotoMatch["presumedLinks"] = [];

  // 1. Les liens portés par les marqueurs.
  const linkedPhotoIds = new Set<number>();
  for (const marker of markers) {
    for (const id of marker.photoIds) {
      if (!photoById.has(id)) continue;
      linkedPhotoIds.add(id);
      covered.add(marker.handle);
      markersByPhoto.get(id)?.push(marker.handle);
    }
  }

  // 2. Ce qui reste : photos sans lien, marqueurs sans photo liée.
  const freePhotos = photos.filter((photo) => !linkedPhotoIds.has(photo.id));
  const freeMarkers = markers.filter((marker) => !covered.has(marker.handle));
  const presume = (marker: MatchableEntity, photoId: number): void => {
    covered.add(marker.handle);
    markersByPhoto.get(photoId)?.push(marker.handle);
    presumedLinks.push({ handle: marker.handle, photoId });
  };

  if (markers.length === 1) {
    for (const photo of freePhotos) presume(markers[0], photo.id);
  } else {
    let hasUndecidedPhoto = false;
    for (const photo of freePhotos) {
      const number = photo.photoNumber ?? null;
      const candidates =
        number === null
          ? []
          : freeMarkers.filter((marker) => marker.markerNumber === number);
      if (candidates.length === 1) presume(candidates[0], photo.id);
      else if (candidates.length > 1)
        candidates.forEach((marker) => toConfirm.add(marker.handle));
      else hasUndecidedPhoto = true;
    }
    if (hasUndecidedPhoto) {
      for (const marker of freeMarkers) toConfirm.add(marker.handle);
    }
  }

  const markerStates = new Map<string, MarkerPhotoState>();
  const uncoveredMarkers: MarkerSlot[] = [];
  for (const marker of markers) {
    if (covered.has(marker.handle)) {
      markerStates.set(marker.handle, "covered");
      continue;
    }
    markerStates.set(
      marker.handle,
      toConfirm.has(marker.handle) ? "toConfirm" : "missing",
    );
    uncoveredMarkers.push(slotOf(marker));
  }

  return {
    markerStates,
    uncoveredMarkers,
    choosableMarkers: markers.length > 1 ? markers.map(slotOf) : [],
    markersByPhoto,
    presumedLinks,
    soleMarkerHandle: markers.length === 1 ? markers[0].handle : null,
  };
}
